import { Star } from "lucide-react";
import { TrustpilotStar } from "@/components/site/TrustpilotStar";

const BADGES = [
  ["Google", "/logo-google.webp", "h-5 sm:h-6"],
  ["Checkatrade", "/logo-checkatrade.webp", "h-6 sm:h-7"],
  ["TrustATrader", "/logo-trustatrader.webp", "h-8 sm:h-9"],
];

const LINE_ONE = "Highly Rated by Customers.";
const LINE_TWO = "Trusted to Protect Your Home.";

const Stars = ({ className = "h-4 w-4" }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {Array.from({ length: 5 }).map((_, index) => (
      <Star key={index} className={`${className} fill-[#F5A623] text-[#F5A623]`} />
    ))}
  </span>
);

const Logos = ({ className = "" }) => (
  <span className={`flex flex-wrap items-center gap-x-5 gap-y-3 ${className}`}>
    {BADGES.map(([name, src, height]) => (
      <img key={name} src={src} alt={`${name} reviews`} loading="lazy" decoding="async"
           className={`${height} w-auto opacity-90 transition-opacity group-hover:opacity-100`} />
    ))}
    <TrustpilotStar className="h-6 w-6 sm:h-7 sm:w-7" />
  </span>
);

/**
 * Brief reassurance in the hero: the rating, the promise and where the reviews come from.
 * variant: "stacked" (card, copy over logos), "inline" (one open row) or "split" (copy beside logos).
 */
export const TrustBadges = ({ reviewUrl = "", testId = "hero-trust-badges", variant = "stacked" }) => {
  const copy = (
    <span className="block leading-snug">
      <span className="block text-sm font-semibold text-pg-dark">{LINE_ONE}</span>
      <span className="block text-sm text-pg-muted">{LINE_TWO}</span>
    </span>
  );
  const linked = (node) => (reviewUrl
    ? <a href={reviewUrl} target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">{node}</a>
    : node);

  if (variant === "inline") {
    return (
      <div data-testid={testId}
           className="group mt-8 flex w-full flex-col items-start gap-3 border-t border-pg-border pt-5
                      sm:flex-row sm:items-center sm:gap-6">
        {linked(
          <span className="flex items-start gap-3">
            <Stars className="mt-0.5 h-4 w-4" />
            {copy}
          </span>
        )}
        <Logos className="sm:border-l sm:border-pg-border sm:pl-6" />
      </div>
    );
  }

  if (variant === "split") {
    return (
      <div data-testid={testId}
           className="group mt-8 flex w-full flex-col items-start gap-4 rounded-xl border border-pg-border
                      bg-white/80 p-4 backdrop-blur transition-colors hover:border-pg-primary/40
                      sm:w-fit sm:flex-row sm:items-center sm:gap-6 sm:py-3.5">
        {linked(
          <span className="flex flex-col gap-1.5">
            <Stars />
            {copy}
          </span>
        )}
        <Logos className="w-full border-t border-pg-border pt-3 sm:w-auto sm:max-w-[220px]
                          sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0" />
      </div>
    );
  }

  return (
    <div data-testid={testId}
         className="group mt-8 flex w-full flex-col items-start gap-3 rounded-xl border border-pg-border
                    bg-white/80 px-4 py-3.5 backdrop-blur transition-colors
                    hover:border-pg-primary/40 sm:w-auto">
      {linked(
        <span className="flex items-start gap-2.5">
          <Stars className="mt-0.5 h-4 w-4" />
          {copy}
        </span>
      )}
      <Logos className="w-full border-t border-pg-border pt-3" />
    </div>
  );
};
